import { Controller } from "@hotwired/stimulus"

// Live camera capture with a gallery fallback, ported from the legacy
// Camera.tsx. The photo (from the shutter or the file input) is resized to
// at most 1200 px on its longest edge and re-encoded as JPEG at 0.85, posted
// to the upload URL, and the URL that comes back is submitted through the
// hidden form to /scan/parse so Turbo follows the redirect that follows.
const MAX_EDGE_PX = 1200
const JPEG_QUALITY = 0.85

const NO_CAMERA = "This browser doesn't support camera capture. Pick a file instead."
const DENIED = "Camera access denied. Pick a file from your library instead."

export default class extends Controller {
  static targets = [
    "video", "placeholder", "status", "controls", "control", "fileInput", "galleryCta",
    "overlay", "spinner", "overlayText", "overlayError", "retake", "form", "imageUrl"
  ]
  static values = { uploadUrl: String }

  connect() {
    this.facing = "environment"
    this.stream = null
    this.busy = false
    this.startCamera()
  }

  disconnect() {
    this.stopCamera()
  }

  async startCamera() {
    this.stopCamera()
    // The library is on offer until a stream is live: a permission prompt
    // left unanswered must not stand between the user and a photo.
    this.galleryCtaTarget.hidden = false
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.cameraUnavailable(NO_CAMERA)
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: this.facing } },
        audio: false
      })
      // The page moved on (or the camera was flipped) while we waited.
      if (!this.element.isConnected) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      this.stream = stream
      this.videoTarget.srcObject = stream
      this.videoTarget.hidden = false
      this.placeholderTarget.hidden = true
      this.controlsTarget.hidden = false
      this.galleryCtaTarget.hidden = true
      await this.videoTarget.play().catch(() => undefined)
    } catch (err) {
      const name = err && err.name ? err.name : ""
      const message = err instanceof Error ? err.message : "Camera unavailable"
      this.cameraUnavailable(/NotAllowed/.test(name) || /NotAllowed/.test(message) ? DENIED : message)
    }
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop())
      this.stream = null
    }
    if (this.hasVideoTarget) {
      this.videoTarget.srcObject = null
      this.videoTarget.hidden = true
    }
  }

  cameraUnavailable(message) {
    this.statusTarget.textContent = message
    this.placeholderTarget.hidden = false
    this.controlsTarget.hidden = true
    this.galleryCtaTarget.hidden = false
  }

  flip() {
    if (this.busy) return
    this.facing = this.facing === "environment" ? "user" : "environment"
    this.startCamera()
  }

  openGallery() {
    if (this.busy) return
    this.fileInputTarget.click()
  }

  async capture() {
    if (this.busy || !this.stream) return
    this.setBusy(true)
    try {
      const video = this.videoTarget
      const canvas = document.createElement("canvas")
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext("2d")
      if (!ctx) throw new Error("Canvas context unavailable")
      ctx.drawImage(video, 0, 0)
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY))
      if (!blob) throw new Error("Capture failed")
      await this.send(await resizeImage(blob))
    } catch (err) {
      this.fail(err instanceof Error ? err.message : "Capture failed")
    }
  }

  async pickFile(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    this.setBusy(true)
    try {
      await this.send(await resizeImage(file))
    } catch (err) {
      this.fail(err instanceof Error ? err.message : "Resize failed")
    } finally {
      event.target.value = ""
    }
  }

  // Upload, then hand the URL to the server through the hidden form. Turbo
  // submits it and follows the redirect to the picker or the confirm screen.
  async send(blob) {
    this.overlayTextTarget.textContent = "Uploading…"
    const url = await this.upload(blob)
    this.overlayTextTarget.textContent = "Reading label…"
    this.imageUrlTarget.value = url
    this.stopCamera()
    this.formTarget.requestSubmit()
  }

  async upload(blob) {
    const body = new FormData()
    body.append("image", blob, "label.jpg")
    const headers = { Accept: "application/json" }
    const token = document.querySelector('meta[name="csrf-token"]')
    if (token) headers["X-CSRF-Token"] = token.content
    const response = await fetch(this.uploadUrlValue, { method: "POST", body, headers, credentials: "same-origin" })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(uploadError(response.status, data))
    if (!data.url) throw new Error("Upload failed")
    return data.url
  }

  retake() {
    this.overlayTarget.hidden = true
    this.setBusy(false)
    if (!this.stream) this.startCamera()
  }

  setBusy(busy) {
    this.busy = busy
    this.controlTargets.forEach((b) => (b.disabled = busy))
    this.galleryCtaTarget.disabled = busy
    if (busy) {
      this.overlayErrorTarget.hidden = true
      this.retakeTarget.hidden = true
      this.spinnerTarget.hidden = false
      this.overlayTextTarget.hidden = false
      this.overlayTextTarget.textContent = "Reading label…"
      this.overlayTarget.hidden = false
    }
  }

  fail(message) {
    this.busy = false
    this.controlTargets.forEach((b) => (b.disabled = false))
    this.galleryCtaTarget.disabled = false
    this.spinnerTarget.hidden = true
    this.overlayTextTarget.hidden = true
    this.overlayErrorTarget.textContent = message
    this.overlayErrorTarget.hidden = false
    this.retakeTarget.hidden = false
    this.overlayTarget.hidden = false
  }
}

function uploadError(status, data) {
  switch (data && data.error) {
    case "rate_limited":
      return "You've uploaded a lot of photos this hour. Try again later, or enter the wine manually."
    case "too_large":
      return "That photo is too large. Pick a smaller one."
    case "unsupported_type":
      return "That file isn't a JPEG, PNG or WebP image."
    case "demo_read_only_in_taste":
      return data.message
    default:
      return `Upload failed (${status})`
  }
}

// Resize a Blob/File so its longest edge is at most MAX_EDGE_PX, then
// re-encode as JPEG. Always re-encodes, so HEIC/PNG/WebP become one format.
async function resizeImage(input) {
  const bitmap = await createImageBitmap(input)
  const longest = Math.max(bitmap.width, bitmap.height)
  let width = bitmap.width
  let height = bitmap.height
  if (longest > MAX_EDGE_PX) {
    const scale = MAX_EDGE_PX / longest
    width = Math.round(bitmap.width * scale)
    height = Math.round(bitmap.height * scale)
  }
  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    if (longest <= MAX_EDGE_PX) return input
    throw new Error("Resize canvas unavailable")
  }
  ctx.drawImage(bitmap, 0, 0, width, height)
  return canvas.convertToBlob({ type: "image/jpeg", quality: JPEG_QUALITY })
}
